/**
 * Curated catalog of normative references shown in the public Normas page.
 *
 * Hand-curated, NOT derived from the raw filesystem dump in
 * `@alphaconcreto/data-normas`. Each entry has friendly copy, an engineer
 * speech-bubble explanation, and a list of modules where the norm is applied.
 */

export type NormCategory = 'norma-peru' | 'codigo-internacional' | 'bibliografia';

export interface NormHighlight {
  label: string;
  value: string;
}

export interface NormEntry {
  id: string;
  code: string;            // e.g. "NTE E.060"
  title: string;           // friendly title
  scope: string;           // 1-line scope
  description: string;     // 2-3 sentence plain-language explanation
  engineerSays: string;    // first-person quote, conversational
  category: NormCategory;
  illustration: 'concrete' | 'seismic' | 'loads' | 'foundation' | 'masonry' | 'aci-book' | 'pucp' | 'sap';
  highlights: NormHighlight[];
  appliedIn: { module: string; what: string }[];
  /** path inside data-normas to surface the raw data on demand (advanced view) */
  dataPath?: string;
}

export interface NormCategoryGroup {
  id: NormCategory;
  title: string;
  subtitle: string;
  entries: NormEntry[];
}

const NORMS_PERU: NormEntry[] = [
  {
    id: 'e060',
    code: 'NTE E.060',
    title: 'Concreto Armado',
    scope: 'Diseno y construccion de elementos de concreto armado en edificaciones.',
    description:
      'Es la norma que define como diseñar columnas, vigas, losas, muros y zapatas de concreto armado en Peru. Incluye factores de reduccion de resistencia, cuantias minimas y maximas, requisitos de anclaje, recubrimientos y combinaciones de carga.',
    engineerSays:
      'Cada vez que calculas una columna en AlphaConcreto, estoy verificando contra E.060: que la cuantia este entre 1% y 6%, que el bloque equivalente de Whitney sea correcto y que el factor phi se aplique segun la deformacion en el acero traccionado.',
    category: 'norma-peru',
    illustration: 'concrete',
    highlights: [
      { label: 'Cuantia minima', value: 'rho >= 1.00 %' },
      { label: 'Cuantia maxima', value: 'rho <= 6.00 %' },
      { label: 'Bloque equivalente', value: 'beta1 = 0.85 (f c <= 280)' },
      { label: 'Factor phi (compresion)', value: '0.65 a 0.90' },
    ],
    appliedIn: [
      { module: 'Diseno de columnas', what: 'verificacion de cuantias y diagrama P-M' },
      { module: 'Vigas (proximamente)', what: 'flexion, corte, anclaje de barras' },
      { module: 'Losas (proximamente)', what: 'refuerzo positivo y negativo' },
      { module: 'Zapatas (proximamente)', what: 'punzonamiento y refuerzo de fondo' },
    ],
    dataPath: 'estructuras/reglas-codigo/E060-concreto-armado.json',
  },
  {
    id: 'e030',
    code: 'NTE E.030',
    title: 'Diseno Sismorresistente',
    scope: 'Define el espectro de diseno y los factores para analisis sismico de edificaciones.',
    description:
      'Establece los parametros para calcular las fuerzas sismicas que actuan sobre una edificacion en Peru. Define la zonificacion sismica (Z), el factor de uso (U), el factor de suelo (S), el periodo fundamental, el factor de amplificacion sismica (C) y el coeficiente de reduccion de fuerzas sismicas (R).',
    engineerSays:
      'Cuando cargues sismo X o Y como Mu en el modulo de columnas, esos momentos vienen de un analisis E.030. Aqui tienes los factores Z, U, S y los coeficientes R por sistema estructural para que armes las combinaciones correctas.',
    category: 'norma-peru',
    illustration: 'seismic',
    highlights: [
      { label: 'Zonas sismicas', value: '4 (Z = 0.10 a 0.45)' },
      { label: 'Factor de uso', value: 'U = 1.0 a 1.5' },
      { label: 'Coeficiente R (porticos)', value: 'R0 = 8' },
      { label: 'Coeficiente R (muros)', value: 'R0 = 6' },
    ],
    appliedIn: [
      { module: 'Diseno de columnas', what: 'cargas Mu_x, Mu_y de sismo X / Y' },
      { module: 'Predimensionamiento', what: 'control de derivas y rigideces' },
      { module: 'Muros estructurales', what: 'flexocompresion + corte sismico' },
    ],
    dataPath: 'estructuras/reglas-codigo/E030-sismorresistente.json',
  },
  {
    id: 'e020',
    code: 'NTE E.020',
    title: 'Cargas',
    scope: 'Cargas muertas y vivas para diseno estructural.',
    description:
      'Tabla las cargas vivas minimas para cada tipo de uso (vivienda, oficinas, escuelas, hospitales, etc.) y los pesos especificos de los materiales mas comunes. Es la entrada principal para cualquier predimensionamiento.',
    engineerSays:
      'No inventes las cargas. Si tu proyecto es vivienda son 200 kgf/m2; si es escuela 250; si es almacen 500. Aqui esta la tabla completa para que el predim arranque bien.',
    category: 'norma-peru',
    illustration: 'loads',
    highlights: [
      { label: 'Vivienda', value: '200 kgf/m2' },
      { label: 'Oficinas', value: '250 kgf/m2' },
      { label: 'Escuelas (aulas)', value: '250 kgf/m2' },
      { label: 'Almacenes / tiendas', value: '500 kgf/m2' },
    ],
    appliedIn: [
      { module: 'Predimensionamiento', what: 'cargas vivas por uso' },
      { module: 'Vigas, losas, columnas', what: 'combinaciones 1.4D + 1.7L' },
    ],
    dataPath: 'predimensionamiento/reglas-codigo/predimensionado.json',
  },
  {
    id: 'e050',
    code: 'NTE E.050',
    title: 'Suelos y Cimentaciones',
    scope: 'Estudios de mecanica de suelos y diseno geotecnico de cimentaciones.',
    description:
      'Establece los requisitos para el estudio del terreno, la capacidad portante admisible, los asentamientos permisibles y el diseno geotecnico de zapatas, plateas y pilotes.',
    engineerSays:
      'Antes de pre-dimensionar una zapata necesitas un EMS. La capacidad admisible que ahi sale entra como input principal del modulo de zapatas (proximamente).',
    category: 'norma-peru',
    illustration: 'foundation',
    highlights: [
      { label: 'Asentamiento maximo (zapata)', value: '1 pulg (~2.5 cm)' },
      { label: 'Profundidad minima', value: 'Df >= 1.0 m' },
      { label: 'Factor de seguridad', value: 'FS >= 3.0' },
    ],
    appliedIn: [
      { module: 'Zapatas (proximamente)', what: 'qadm y predim de area' },
      { module: 'Predimensionamiento', what: 'restricciones por suelo' },
    ],
  },
  {
    id: 'e070',
    code: 'NTE E.070',
    title: 'Albanileria',
    scope: 'Diseno y construccion de muros de albanileria confinada y armada.',
    description:
      'Norma especifica para edificaciones de albanileria. Define la densidad minima de muros por piso, la diferencia entre muros portantes y tabiques, y las verificaciones de resistencia al corte.',
    engineerSays:
      'En vivienda informal mucha gente confunde tabique y muro portante. La norma E.070 te dice cuanto muro estructural necesitas por piso para que el sismo no te tumbe la casa.',
    category: 'norma-peru',
    illustration: 'masonry',
    highlights: [
      { label: 'Espesor portante minimo', value: 't >= 13 cm' },
      { label: 'Densidad muros (vivienda)', value: 'Area muros / Area piso >= 0.0017 Z U S' },
    ],
    appliedIn: [
      { module: 'Albanileria (proximamente)', what: 'predim densidad y verificacion corte' },
    ],
  },
];

const NORMS_INTERNATIONAL: NormEntry[] = [
  {
    id: 'aci318',
    code: 'ACI 318-19',
    title: 'Building Code Requirements for Structural Concrete',
    scope: 'Codigo americano de referencia para concreto estructural.',
    description:
      'La norma E.060 peruana esta basada en el ACI 318. Cuando hay vacios o ambiguedades en E.060, ACI 318 es la referencia consultada. Define los mismos conceptos pero con mayor detalle: bloque equivalente, factores phi, anclajes, etc.',
    engineerSays:
      'Si quieres entender por que E.060 dice algo, ACI 318 te explica. Trabajo con ambas: E.060 es el codigo aplicable, ACI 318 es la referencia tecnica.',
    category: 'codigo-internacional',
    illustration: 'aci-book',
    highlights: [
      { label: 'Bloque equivalente Whitney', value: 'a = beta1 c' },
      { label: 'beta1', value: '0.85 (fc <= 4000 psi)' },
      { label: 'Strain limit (tied col.)', value: 'eps_t >= 0.005 -> phi = 0.90' },
      { label: 'Pn,max (tied)', value: '0.80 [0.85 fc (Ag - As) + fy As]' },
    ],
    appliedIn: [
      { module: 'Diseno de columnas', what: 'algoritmo de diagrama P-M y factor phi' },
    ],
  },
];

const BIBLIOGRAPHY: NormEntry[] = [
  {
    id: 'harmsen',
    code: 'Harmsen 2005',
    title: 'Diseño de Estructuras de Concreto Armado (PUCP)',
    scope: 'Texto base de la PUCP para diseno de concreto armado en Peru.',
    description:
      'El libro de referencia para predimensionamiento y diseno de concreto armado peruano. Incluye tablas de predim de vigas, losas, columnas; cuantia balanceada; longitudes de anclaje; y muchas formulas practicas.',
    engineerSays:
      'Para tablas rapidas de predim (h_viga = L/12, peralte de losa aligerada por luz, etc.) Harmsen es la primera referencia. Lo aplicamos en el wizard de predimensionamiento.',
    category: 'bibliografia',
    illustration: 'pucp',
    highlights: [
      { label: 'Predim viga peraltada', value: 'h ~ L / 12' },
      { label: 'Predim losa aligerada', value: 'h = L / 25' },
      { label: 'Anclaje #5 (5/8")', value: 'Ld ~ 60 cm (fc=210)' },
    ],
    appliedIn: [
      { module: 'Predimensionamiento (proximamente)', what: 'tablas y formulas de predim' },
      { module: 'Diseno de columnas', what: 'verificacion de cuantia balanceada' },
    ],
    dataPath: 'concreto-armado/destrozando/harmsen-concreto-armado-pucp/json/formulas.json',
  },
  {
    id: 'blanco-blasco',
    code: 'Blanco Blasco',
    title: 'Estructuracion y Diseno de Edificaciones de Concreto Armado',
    scope: 'Filosofia sismorresistente y reglas de estructuracion.',
    description:
      'Texto practico que explica como concebir una estructura sismorresistente: ubicacion de placas, simetria, regularidad, irregularidades. Es la guia para que el predimensionamiento no sea solo numerico sino conceptualmente correcto.',
    engineerSays:
      'Antes de calcular nada, hay que estructurar. Aqui te enseno donde van las placas, como evitar las 12 irregularidades de E.030 y como elegir el sistema estructural correcto.',
    category: 'bibliografia',
    illustration: 'pucp',
    highlights: [
      { label: 'Irregularidades en planta', value: '6 tipos (E.030 Tabla 9)' },
      { label: 'Irregularidades en altura', value: '6 tipos (E.030 Tabla 8)' },
      { label: 'Densidad de muros minima', value: 'depende de Z y U' },
    ],
    appliedIn: [
      { module: 'Predimensionamiento (proximamente)', what: 'check de irregularidades' },
    ],
    dataPath: 'estructuras/destrozando/blanco-blasco-estructuracion-diseno/json/predimensionamiento-tablas.json',
  },
  {
    id: 'otazzi',
    code: 'Otazzi',
    title: 'Apuntes del Curso de Concreto Armado (PUCP)',
    scope: 'Profundizacion en temas especificos: aligerados, anclajes, cortante.',
    description:
      'Complemento al Harmsen. Especialmente util para losas aligeradas peruanas (sistema vigueta + ladrillo + loseta) y para detalles de armado en zonas criticas.',
    engineerSays:
      'Para losas aligeradas peruanas (las que tienen ladrillo de techo) este libro es referencia obligatoria. La losa maciza es facil; la aligerada tiene reglas particulares de armado.',
    category: 'bibliografia',
    illustration: 'pucp',
    highlights: [
      { label: 'Aligerada h=20cm', value: 'peso ~ 300 kgf/m2' },
      { label: 'Vigueta separacion', value: 's = 40 cm' },
    ],
    appliedIn: [
      { module: 'Losas (proximamente)', what: 'predim de aligerados peruanos' },
    ],
    dataPath: 'estructuras/destrozando/otazzi-concreto-armado-PUCP/json/conceptos-aporte.json',
  },
  {
    id: 'villarreal',
    code: 'Villarreal',
    title: 'Analisis Sismico y Resistencia de Materiales',
    scope: 'Metodos de analisis sismico estatico y dinamico, dinamica estructural.',
    description:
      'Texto detallado para entender los procedimientos del analisis sismico (estatico equivalente, modal espectral, tiempo-historia). Tambien tiene un volumen sobre resistencia de materiales aplicado a estructuras.',
    engineerSays:
      'Cuando E.030 te diga "aplique el procedimiento de analisis dinamico modal espectral", aqui esta como hacerlo paso a paso. El motor de analisis sismico se basa en estas formulas.',
    category: 'bibliografia',
    illustration: 'sap',
    highlights: [
      { label: 'Periodo fundamental', value: 'T = h_n / Ct' },
      { label: 'Cortante basal', value: 'V = Z U C S P / R' },
    ],
    appliedIn: [
      { module: 'Modulo sismico (proximamente)', what: 'analisis estatico y modal' },
    ],
    dataPath: 'estructuras/destrozando/villarreal-analisis-sismico/json/factores-E030.json',
  },
];

export const NORM_GROUPS: NormCategoryGroup[] = [
  {
    id: 'norma-peru',
    title: 'Normas Tecnicas Peruanas (NTE)',
    subtitle: 'Reglamento Nacional de Edificaciones - aplicacion obligatoria.',
    entries: NORMS_PERU,
  },
  {
    id: 'codigo-internacional',
    title: 'Codigos internacionales de referencia',
    subtitle: 'Base tecnica de las normas peruanas.',
    entries: NORMS_INTERNATIONAL,
  },
  {
    id: 'bibliografia',
    title: 'Bibliografia tecnica',
    subtitle: 'Libros que el motor de calculo cita y aplica.',
    entries: BIBLIOGRAPHY,
  },
];
